/**
 * Car + enemy rendering — uses pixel-art sprite images for the player cars,
 * vector cartoon drawings for cops/FBI/pedestrians.
 */
import { OUTLINE, roundRect } from "./itemVisuals";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Lazy-loaded sprite cache: kind -> HTMLImageElement.
// First call to drawCar triggers loading. Until ready, the car falls back to
// the vector drawing so there's no blank frame.
//
// Sprites pre-processed once on load:
//   1. crossOrigin="anonymous" so drawing onto a canvas doesn't taint it
//      (drawCarSprite uses getImageData which throws SecurityError on a
//      tainted canvas — this was the iteration-30 production crash).
//   2. After load we run the alpha-threshold ONCE and stash the resulting
//      offscreen canvas in `_solidCache` so per-frame rendering only does
//      one drawImage + one tint fill — no per-pixel ops on the hot path.
const _spriteCache = {};
const _solidCache = {};  // kind -> { canvas, w, h }
function getSprite(kind) {
  if (_spriteCache[kind] !== undefined) return _spriteCache[kind];
  const img = new Image();
  img.crossOrigin = "anonymous";  // critical: keeps the canvas un-tainted
  img.onload = () => {
    _spriteCache[kind] = img;
    _solidCache[kind] = _buildSolid(img);
  };
  img.onerror = () => { _spriteCache[kind] = null; };
  // Hand-drawn TOP-DOWN sprite — bird's-eye view from your reference sheet.
  // Pre-flipped during slice so angle=0 (positive X) corresponds to "facing
  // right". Game's existing rotation math just works.
  // v=9: load with crossOrigin="anonymous" to keep the canvas un-tainted
  // (iteration 36 fix). Bumped so browsers don't reuse the previous
  // non-CORS cached response (which would still taint).
  const v = 10;
  img.src = `${BACKEND_URL}/api/static/sprites/car_${kind}_top.png?v=${v}`;
  _spriteCache[kind] = "loading";
  return "loading";
}

// Build the alpha-thresholded silhouette ONCE per sprite. Cars look "faded
// and see through" without this — ~50% of the pixels inside the bbox have
// partial alpha (AA + chroma-key noise) which lets the road texture bleed
// through. We snap any interior alpha > 24 to fully opaque, then cache.
//
// Cache key is the sprite kind. Returns { canvas, w, h } where canvas is
// a same-size offscreen, paint-aligned to (0,0).
function _buildSolid(img) {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const ctx = c.getContext("2d");
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(img, 0, 0);
  try {
    const data = ctx.getImageData(0, 0, w, h);
    const buf = data.data;
    for (let i = 3; i < buf.length; i += 4) {
      if (buf[i] > 24) buf[i] = 255;
    }
    ctx.putImageData(data, 0, 0);
  } catch (_e) {
    // If the canvas still ends up tainted (e.g. a CDN serves the sprite
    // without ACAO headers), we silently fall back to the un-thresholded
    // sprite. Tint still applies; the car just looks slightly translucent
    // — far better than a black, frozen game.
  }
  return { canvas: c, w, h };
}

// Cache of (kind, body, accent) -> pre-rendered "outlined" sprite. The
// outline is drawn by stamping a body-colored copy of the silhouette in
// 8 surrounding offsets (radius BODY_OUTLINE_PX), then a slightly
// smaller accent-colored stamp (radius ACCENT_OUTLINE_PX), then the
// ORIGINAL sprite on top — leaving the user's body color as a thick
// halo around the car and the accent color as a thinner inner ring.
// The original art is therefore visible AT FULL DETAIL, which is what
// the user asked for: "outline around the car not on top of it".
const BODY_OUTLINE_PX = 6;
const ACCENT_OUTLINE_PX = 3;
const _outlineCache = {};

function _buildSilhouette(solidCanvas, w, h, color) {
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const ctx = c.getContext("2d");
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(solidCanvas, 0, 0);
  ctx.globalCompositeOperation = "source-in";
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, w, h);
  return c;
}

// Body-tinted sprite — original art with a body-color wash applied
// only inside the silhouette via source-atop. Alpha 0.55 keeps the
// hand-painted shading + highlights visible underneath the user's
// chosen body color. The accent halo (painted later, OUTSIDE the
// silhouette in surrounding offsets) is unaffected.
function _buildBodyTintedSprite(solidCanvas, w, h, color, alpha = 0.55) {
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const ctx = c.getContext("2d");
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(solidCanvas, 0, 0);
  ctx.globalCompositeOperation = "source-atop";
  ctx.globalAlpha = alpha;
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, w, h);
  return c;
}

function _buildOutlinedSprite(kind, img, body, accent) {
  const cacheKey = `${kind}|${body}|${accent}`;
  const hit = _outlineCache[cacheKey];
  if (hit) return hit;
  const solid = _solidCache[kind];
  if (!solid) return null; // not ready — caller will fall back

  const PAD = BODY_OUTLINE_PX + 2;
  const w = solid.w + PAD * 2;
  const h = solid.h + PAD * 2;
  const out = document.createElement("canvas");
  out.width = w; out.height = h;
  const octx = out.getContext("2d");
  octx.imageSmoothingEnabled = false;

  // Pre-color silhouettes once.
  const bodySilhouette = body ? _buildSilhouette(solid.canvas, solid.w, solid.h, body) : null;
  const accentSilhouette = accent ? _buildSilhouette(solid.canvas, solid.w, solid.h, accent) : null;

  // 1. Body halo — stamp body-colored silhouette in a circle of offsets.
  if (bodySilhouette) {
    const r = BODY_OUTLINE_PX;
    for (let dx = -r; dx <= r; dx++) {
      for (let dy = -r; dy <= r; dy++) {
        if (dx * dx + dy * dy > r * r) continue;
        if (dx === 0 && dy === 0) continue;
        octx.drawImage(bodySilhouette, PAD + dx, PAD + dy);
      }
    }
  }
  // 2. Accent halo — same trick at a smaller radius. Paints over part
  // of the body halo so a body ring remains on the outside, accent ring
  // sits inside it — clean two-tone reading.
  if (accentSilhouette) {
    const r = ACCENT_OUTLINE_PX;
    for (let dx = -r; dx <= r; dx++) {
      for (let dy = -r; dy <= r; dy++) {
        if (dx * dx + dy * dy > r * r) continue;
        if (dx === 0 && dy === 0) continue;
        octx.drawImage(accentSilhouette, PAD + dx, PAD + dy);
      }
    }
  }
  // 3. Body-tinted original sprite on top — preserves the hand-painted
  //    AI shading + highlights AND lets the user's body color read on
  //    the entire car body (per user request: "the body color selection
  //    should tint the whole body of the car"). Source-atop confines
  //    the tint to the silhouette so the surrounding accent halo
  //    (painted in step 2 outside the silhouette) is untouched.
  if (solid && _solidCache[kind]) {
    if (body) {
      const tinted = _buildBodyTintedSprite(solid.canvas, solid.w, solid.h, body);
      octx.drawImage(tinted, PAD, PAD);
    } else {
      octx.drawImage(solid.canvas, PAD, PAD);
    }
  } else {
    octx.drawImage(img, PAD, PAD);
  }

  const entry = { canvas: out, w, h, pad: PAD, srcW: solid.w, srcH: solid.h };
  _outlineCache[cacheKey] = entry;
  // Cap cache size — at most ~25 entries (5 chassis × ~5 distinct color
  // pairs in normal use) so we don't grow forever during a marathon
  // session of color-tweaking in the Garage.
  const keys = Object.keys(_outlineCache);
  if (keys.length > 40) {
    delete _outlineCache[keys[0]];
  }
  return entry;
}

export function drawCar(ctx, x, y, angle, body, accent, alpha = 1, kind = "cruiser") {
  // Try the pixel-art sprite first. Falls back to the vector drawing if the
  // image isn't ready yet (or fails to load).
  const sprite = getSprite(kind);
  if (sprite && sprite !== "loading" && sprite.complete) {
    return drawCarSprite(ctx, x, y, angle, sprite, alpha, body, accent, kind);
  }
  if (kind === "knight")  return drawKnightCar(ctx, x, y, angle, body, accent, alpha);
  if (kind === "tank")    return drawTankCar(ctx, x, y, angle, body, accent, alpha);
  if (kind === "toon")    return drawToonCar(ctx, x, y, angle, body, accent, alpha);
  if (kind === "hotrod")  return drawHotrodCar(ctx, x, y, angle, body, accent, alpha);
  if (kind === "viper")   return drawViperCar(ctx, x, y, angle, body, accent, alpha);
  return drawCruiserCar(ctx, x, y, angle, body, accent, alpha);
}

// Blit a pixel-art car sprite at (x, y) rotated by `angle`. The sprite faces
// right (positive X) at angle 0. Sprites are pre-flipped during slice so
// "right" is forward.
//
// Per-frame pipeline (HOT PATH — keep this cheap):
//   1. drawImage of the pre-thresholded silhouette (built once on load)
//      into a shared 128×128 offscreen.
//   2. body tint via source-atop (single fillRect, alpha 0.65).
//   3. accent stripe via source-atop (single fillRect).
//   4. blit the offscreen sub-rect into the main canvas.
// No getImageData / putImageData on the hot path. The previous version did
// per-pixel work every frame for every car, which crashed production via
// canvas tainting AND would have hit a perf wall once 8+ cars were on screen.
// Render a car sprite at (x,y) rotated by `angle`. Per-frame pipeline
// (HOT PATH — keep cheap):
//   1. If both body+accent are provided: blit the cached (kind, body,
//      accent) "outlined" sprite — original art untouched, with a body
//      color halo and an accent color inner ring around the silhouette.
//      The user's color choices read as a clean halo, the hand-painted
//      detail of the AI sprite is fully visible (no tint on top).
//   2. Otherwise: blit the un-tinted solid silhouette (or the raw image
//      if the silhouette cache hasn't been built yet — happens during
//      the first frame after sprite load).
// No per-pixel JS on the hot path. The outline canvas was pre-built
// once when the (kind, body, accent) tuple first appeared, then cached.
function drawCarSprite(ctx, x, y, angle, img, alpha = 1, body = null, accent = null, kind = "cruiser") {
  ctx.save();
  ctx.globalAlpha = alpha;
  // Don't force imageSmoothingEnabled = false here — let the caller decide.
  // Race canvas wants pixel-art (chunky); Garage live preview wants smooth.
  ctx.translate(x, y);
  ctx.rotate(angle);
  const targetW = 56;
  const ratio = img.naturalHeight / img.naturalWidth;
  const targetH = targetW * ratio;

  if (body || accent) {
    const outlined = _buildOutlinedSprite(kind, img, body, accent);
    if (outlined) {
      // The outlined canvas includes PAD pixels of halo on every side.
      // Scale the destination so the inner sprite area equals targetW ×
      // targetH while the halo extends naturally past it.
      const scale = targetW / outlined.srcW;
      const dW = outlined.w * scale;
      const dH = outlined.h * scale;
      ctx.drawImage(
        outlined.canvas,
        0, 0, outlined.w, outlined.h,
        -dW / 2, -dH / 2, dW, dH,
      );
      ctx.restore();
      return;
    }
    // Fall through to solid silhouette if outline not ready.
  }

  // Fast path — no recolor work or outline cache miss. Blit the solid
  // silhouette (or raw image) so the car never reads as "see through".
  const solid = _solidCache[kind];
  if (solid) {
    ctx.drawImage(solid.canvas, 0, 0, solid.w, solid.h, -targetW / 2, -targetH / 2, targetW, targetH);
  } else {
    ctx.drawImage(img, -targetW / 2, -targetH / 2, targetW, targetH);
  }
  ctx.restore();
}

// Re-used offscreen canvas — sized to the largest car silhouette we draw.
let _offCanvas = null;
function getOffscreen(w, h) {
  if (!_offCanvas) {
    const c = document.createElement("canvas");
    c.width = 128; c.height = 128;
    _offCanvas = { canvas: c, ctx: c.getContext("2d") };
  }
  if (_offCanvas.canvas.width < w || _offCanvas.canvas.height < h) {
    _offCanvas.canvas.width = Math.max(w, _offCanvas.canvas.width);
    _offCanvas.canvas.height = Math.max(h, _offCanvas.canvas.height);
  }
  return _offCanvas;
}

function drawCruiserCar(ctx, x, y, angle, body, accent, alpha = 1) {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.lineJoin = "round";
  ctx.lineCap = "round";

  // shadow
  ctx.fillStyle = "rgba(0,0,0,0.3)";
  ctx.beginPath();
  ctx.ellipse(3, 5, 22, 13, 0, 0, Math.PI * 2);
  ctx.fill();

  // body (fatter, rounder)
  ctx.fillStyle = body;
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 5;
  ctx.beginPath();
  roundRect(ctx, -20, -14, 40, 28, 9);
  ctx.fill();
  ctx.stroke();

  // bumper stripe (accent) across middle
  ctx.fillStyle = accent;
  ctx.fillRect(-20, -4, 40, 3);
  ctx.strokeRect(-20, -4, 40, 3);

  // hood/roof darker zone
  ctx.fillStyle = "rgba(0,0,0,0.15)";
  ctx.beginPath();
  roundRect(ctx, -6, -11, 20, 22, 5);
  ctx.fill();

  // windshield (big cartoon)
  ctx.fillStyle = "#1f2e4a";
  ctx.beginPath();
  roundRect(ctx, -4, -10, 14, 20, 4);
  ctx.fill();
  ctx.stroke();
  // windshield shine
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.fillRect(-3, -9, 4, 12);

  // nose tip (accent nosepiece)
  ctx.fillStyle = accent;
  ctx.beginPath();
  roundRect(ctx, 15, -10, 5, 20, 2);
  ctx.fill();
  ctx.stroke();

  // headlight "eyes" (googly)
  const t = performance.now() / 200;
  const eyeBob = Math.sin(t) * 0.6;
  ctx.fillStyle = "#FFFFFF";
  ctx.lineWidth = 3;
  ctx.beginPath(); ctx.arc(14, -8, 4, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
  ctx.beginPath(); ctx.arc(14, 8, 4, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
  ctx.fillStyle = "#1a1a1a";
  ctx.beginPath(); ctx.arc(15, -8 + eyeBob, 1.8, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(15, 8 + eyeBob, 1.8, 0, Math.PI * 2); ctx.fill();

  // tail lights
  ctx.fillStyle = "#E63946";
  ctx.fillRect(-21, -11, 3, 5);
  ctx.fillRect(-21, 6, 3, 5);
  ctx.strokeRect(-21, -11, 3, 5);
  ctx.strokeRect(-21, 6, 3, 5);

  // wheels (chunky black bars)
  ctx.fillStyle = OUTLINE;
  ctx.fillRect(-16, -17, 10, 6);
  ctx.fillRect(-16, 11, 10, 6);
  ctx.fillRect(6, -17, 10, 6);
  ctx.fillRect(6, 11, 10, 6);
  // tiny rim highlights
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(-14, -16, 2, 1);
  ctx.fillRect(-14, 12, 2, 1);
  ctx.fillRect(8, -16, 2, 1);
  ctx.fillRect(8, 12, 2, 1);

  ctx.restore();
}

export function drawBoostFlames(ctx, p) {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.angle);
  ctx.lineJoin = "round";
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 2.5;
  const colors = ["#FFD166", "#F5711B", "#E63946"];
  for (let i = 0; i < 6; i++) {
    const len = 12 + Math.random() * 14;
    ctx.fillStyle = colors[i % colors.length];
    ctx.beginPath();
    ctx.moveTo(-19, -5 + Math.random() * 10);
    ctx.lineTo(-19 - len, -3 + Math.random() * 4);
    ctx.lineTo(-19, 5 - Math.random() * 10);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
  ctx.restore();
}

export function drawDustPuffs(ctx, p) {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.angle);
  ctx.fillStyle = "#E8E4D9";
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 2;
  const t = performance.now();
  for (let i = 0; i < 3; i++) {
    const age = ((t / 80 + i * 7) % 15) / 15;
    const size = 3 + age * 10;
    const offset = -20 - age * 14;
    ctx.globalAlpha = 0.7 * (1 - age);
    ctx.beginPath();
    ctx.arc(offset, (i - 1) * 6, size, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }
  ctx.restore();
}

// Enemy sprite cache (cop + fbi top-down PNGs generated via Nano Banana).
const _enemySpriteCache = {};
function getEnemySprite(type) {
  if (_enemySpriteCache[type] !== undefined) return _enemySpriteCache[type];
  const img = new Image();
  img.crossOrigin = "anonymous";  // keep canvas un-tainted (defense in depth)
  img.onload = () => { _enemySpriteCache[type] = img; };
  img.onerror = () => { _enemySpriteCache[type] = null; };
  // v=7: load with crossOrigin="anonymous" — bumped so browsers don't
  // reuse a non-CORS cached response (iteration 36 fix).
  img.src = `${BACKEND_URL}/api/static/sprites/${type}_top.png?v=7`;
  _enemySpriteCache[type] = "loading";
  return "loading";
}

export function drawEnemy(ctx, e) {
  const sprite = getEnemySprite(e.type);
  if (sprite && sprite !== "loading" && sprite.complete) {
    // Pixel-art top-down cop/FBI sprite (same style as player cars).
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.translate(e.x, e.y);
    ctx.rotate(e.angle);
    const targetW = 56;
    const ratio = sprite.naturalHeight / sprite.naturalWidth;
    const targetH = targetW * ratio;
    ctx.drawImage(sprite, -targetW / 2, -targetH / 2, targetW, targetH);
    ctx.restore();
    return;
  }
  // Fallback vector drawing while the PNG is still loading.
  ctx.save();
  ctx.translate(e.x, e.y);
  ctx.rotate(e.angle);
  ctx.lineJoin = "round";
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 5;

  // shadow
  ctx.fillStyle = "rgba(0,0,0,0.3)";
  ctx.beginPath();
  ctx.ellipse(3, 5, 22, 13, 0, 0, Math.PI * 2);
  ctx.fill();

  const bodyColor = e.type === "fbi" ? "#1a1a1a" : "#1e4d8f";
  ctx.fillStyle = bodyColor;
  ctx.beginPath();
  roundRect(ctx, -20, -14, 40, 28, 9);
  ctx.fill();
  ctx.stroke();

  // stripe
  ctx.fillStyle = e.type === "fbi" ? "#FFD700" : "#FFFFFF";
  ctx.fillRect(-20, -3, 40, 5);
  ctx.strokeRect(-20, -3, 40, 5);

  // windshield
  ctx.fillStyle = "#1f2e4a";
  ctx.beginPath();
  roundRect(ctx, -4, -10, 14, 20, 4);
  ctx.fill();
  ctx.stroke();

  // angry eyes (headlights)
  ctx.fillStyle = "#FFFFFF";
  ctx.lineWidth = 3;
  ctx.beginPath(); ctx.arc(14, -8, 4, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
  ctx.beginPath(); ctx.arc(14, 8, 4, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
  ctx.fillStyle = "#1a1a1a";
  // angled pupil (angry look)
  ctx.beginPath(); ctx.arc(16, -7, 1.8, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(16, 9, 1.8, 0, Math.PI * 2); ctx.fill();
  // angry eyebrows
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(11, -12); ctx.lineTo(17, -10);
  ctx.moveTo(17, 4); ctx.lineTo(11, 6);
  ctx.stroke();

  // lightbar flashing
  const flash = Math.floor(performance.now() / 160) % 2 === 0;
  ctx.fillStyle = flash ? "#E63946" : "#3B82F6";
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 2;
  ctx.beginPath();
  roundRect(ctx, -10, -19, 20, 6, 2);
  ctx.fill(); ctx.stroke();

  // wheels
  ctx.fillStyle = OUTLINE;
  ctx.fillRect(-16, -17, 10, 6);
  ctx.fillRect(-16, 11, 10, 6);
  ctx.fillRect(6, -17, 10, 6);
  ctx.fillRect(6, 11, 10, 6);

  ctx.restore();

  // Big cartoon label with double outline
  ctx.save();
  ctx.font = "bold 13px 'Anton'";
  ctx.textAlign = "center";
  ctx.lineJoin = "round";
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 4;
  const label = e.type.toUpperCase();
  ctx.strokeText(label, e.x, e.y - 24);
  ctx.fillStyle = e.type === "fbi" ? "#FFD700" : "#FFFFFF";
  ctx.fillText(label, e.x, e.y - 24);
  ctx.restore();
}

export function drawMissile(ctx, m, bodyColor, tipColor) {
  const a = Math.atan2(m.vy, m.vx);
  ctx.save();
  ctx.translate(m.x, m.y);
  ctx.rotate(a);
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 2.5;
  // body
  ctx.fillStyle = bodyColor;
  ctx.beginPath();
  roundRect(ctx, -12, -5, 20, 10, 3);
  ctx.fill();
  ctx.stroke();
  // tip
  ctx.fillStyle = tipColor;
  ctx.beginPath();
  ctx.moveTo(8, -5);
  ctx.lineTo(15, 0);
  ctx.lineTo(8, 5);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  // flame
  ctx.fillStyle = "#FFD166";
  const jitter = Math.random() * 4;
  ctx.beginPath();
  ctx.moveTo(-12, -4);
  ctx.lineTo(-20 - jitter, 0);
  ctx.lineTo(-12, 4);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

export function drawMonkeyBlast(ctx, m) {
  ctx.save();
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 5;
  ctx.fillStyle = "rgba(255, 161, 77, 0.3)";
  ctx.beginPath();
  ctx.arc(m.x, m.y, m.r, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  // jagged "BOOM" outline
  ctx.strokeStyle = "#E63946";
  ctx.lineWidth = 4;
  ctx.beginPath();
  const spikes = 16;
  for (let i = 0; i < spikes; i++) {
    const a = (i / spikes) * Math.PI * 2;
    const r = m.r * (0.7 + (i % 2) * 0.15);
    const px = m.x + Math.cos(a) * r;
    const py = m.y + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.stroke();
  ctx.restore();
}


// ============================================================================
// Knight Hauler — long sleek black hood with sweeping red scanner light.
// Inspired by KITT.
// ============================================================================
function drawKnightCar(ctx, x, y, angle, body, accent, alpha = 1) {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.lineJoin = "round";

  // shadow
  ctx.fillStyle = "rgba(0,0,0,0.35)";
  ctx.beginPath();
  ctx.ellipse(3, 6, 26, 14, 0, 0, Math.PI * 2);
  ctx.fill();

  // long low body
  ctx.fillStyle = body;
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 5;
  ctx.beginPath();
  roundRect(ctx, -24, -12, 48, 24, 6);
  ctx.fill();
  ctx.stroke();

  // hood top stripe
  ctx.fillStyle = "rgba(255,255,255,0.06)";
  ctx.fillRect(-24, -12, 48, 4);

  // angled windshield
  ctx.fillStyle = "#1f2e4a";
  ctx.beginPath();
  ctx.moveTo(-2, -10);
  ctx.lineTo(10, -8);
  ctx.lineTo(10, 8);
  ctx.lineTo(-2, 10);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // SCANNER bar — sweeping red light across the hood (KITT)
  const t = performance.now() / 250;
  const sweep = (Math.sin(t) + 1) / 2; // 0 → 1
  const scanX = 14 + sweep * 8 - 4; // sweeps within hood
  ctx.fillStyle = "#0A0A0A";
  ctx.fillRect(15, -9, 7, 18);
  ctx.strokeRect(15, -9, 7, 18);
  // glowing red dot
  const grad = ctx.createRadialGradient(scanX + 18, 0, 0, scanX + 18, 0, 9);
  grad.addColorStop(0, "#FF3030");
  grad.addColorStop(1, "rgba(255,48,48,0)");
  ctx.fillStyle = grad;
  ctx.fillRect(10, -10, 16, 20);
  ctx.fillStyle = accent;
  ctx.fillRect(scanX + 16, -2, 4, 4);

  // dual exhaust tail lights
  ctx.fillStyle = accent;
  ctx.fillRect(-25, -10, 3, 5);
  ctx.fillRect(-25, 5, 3, 5);
  ctx.strokeRect(-25, -10, 3, 5);
  ctx.strokeRect(-25, 5, 3, 5);

  // wheels (sleek, low profile)
  ctx.fillStyle = OUTLINE;
  ctx.fillRect(-18, -16, 11, 4);
  ctx.fillRect(-18, 12, 11, 4);
  ctx.fillRect(8, -16, 11, 4);
  ctx.fillRect(8, 12, 11, 4);

  ctx.restore();
}

// ============================================================================
// Tank — chunky armored beast with treads + turret antenna.
// ============================================================================
function drawTankCar(ctx, x, y, angle, body, accent, alpha = 1) {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.lineJoin = "round";
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 5;

  // shadow
  ctx.fillStyle = "rgba(0,0,0,0.35)";
  ctx.beginPath();
  ctx.ellipse(2, 8, 28, 18, 0, 0, Math.PI * 2);
  ctx.fill();

  // treads (top + bottom thick rectangles with rivets)
  ctx.fillStyle = "#1F1F1F";
  ctx.fillRect(-24, -22, 48, 8);
  ctx.fillRect(-24, 14, 48, 8);
  ctx.strokeRect(-24, -22, 48, 8);
  ctx.strokeRect(-24, 14, 48, 8);
  // tread cleats
  ctx.fillStyle = accent;
  for (let i = -22; i < 24; i += 8) {
    ctx.fillRect(i, -21, 4, 6);
    ctx.fillRect(i, 15, 4, 6);
  }

  // hull
  ctx.fillStyle = body;
  ctx.beginPath();
  roundRect(ctx, -22, -14, 44, 28, 4);
  ctx.fill();
  ctx.stroke();

  // diagonal armor plating highlights
  ctx.fillStyle = "rgba(255,255,255,0.08)";
  ctx.beginPath();
  ctx.moveTo(-22, -14);
  ctx.lineTo(0, -14);
  ctx.lineTo(-12, 14);
  ctx.lineTo(-22, 14);
  ctx.closePath();
  ctx.fill();

  // turret (rotates slowly with cosmetic bobble)
  const t = performance.now() / 1000;
  const turretA = Math.sin(t) * 0.15;
  ctx.save();
  ctx.rotate(turretA);
  ctx.fillStyle = "#3F4A26";
  ctx.beginPath();
  ctx.arc(0, 0, 10, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  // gun barrel forward
  ctx.fillStyle = OUTLINE;
  ctx.fillRect(8, -3, 16, 6);
  ctx.strokeRect(8, -3, 16, 6);
  // muzzle
  ctx.fillStyle = accent;
  ctx.fillRect(22, -4, 4, 8);
  ctx.strokeRect(22, -4, 4, 8);
  ctx.restore();

  // antenna
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(-8, -12);
  ctx.lineTo(-12, -22);
  ctx.stroke();
  ctx.fillStyle = "#E63946";
  ctx.beginPath();
  ctx.arc(-12, -22, 2, 0, Math.PI * 2);
  ctx.fill();

  // angry slit "eye" instead of headlights
  ctx.fillStyle = "#FFDC00";
  ctx.fillRect(18, -2, 5, 4);
  ctx.strokeRect(18, -2, 5, 4);

  ctx.restore();
}

// ============================================================================
// Toonster — round bug-eyed Roger Rabbit-style cab.
// ============================================================================
function drawToonCar(ctx, x, y, angle, body, accent, alpha = 1) {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.lineJoin = "round";
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 5;

  // wobble
  const t = performance.now() / 200;
  const wobble = Math.sin(t) * 1.2;
  ctx.translate(0, wobble);

  // shadow
  ctx.fillStyle = "rgba(0,0,0,0.3)";
  ctx.beginPath();
  ctx.ellipse(2, 7 - wobble, 24, 12, 0, 0, Math.PI * 2);
  ctx.fill();

  // bulbous body — egg shape
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.ellipse(0, 0, 22, 16, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  // checker stripe across middle
  ctx.fillStyle = accent;
  for (let i = -22; i < 22; i += 6) {
    if (Math.floor((i + 22) / 6) % 2 === 0) ctx.fillRect(i, -2, 6, 4);
  }

  // BIG cartoon hood eyes
  const eyeBob = Math.sin(t * 2) * 1.5;
  ctx.fillStyle = "#FFFFFF";
  ctx.beginPath();
  ctx.arc(11, -6, 7, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(11, 6, 7, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  // big black pupils (googly)
  ctx.fillStyle = OUTLINE;
  ctx.beginPath();
  ctx.arc(13, -6 + eyeBob, 3.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(13, 6 + eyeBob, 3.2, 0, Math.PI * 2);
  ctx.fill();
  // shine dots
  ctx.fillStyle = "#FFFFFF";
  ctx.beginPath();
  ctx.arc(14.5, -7 + eyeBob, 1, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(14.5, 5 + eyeBob, 1, 0, Math.PI * 2);
  ctx.fill();

  // wide grin grille
  ctx.fillStyle = "#FFFFFF";
  ctx.beginPath();
  ctx.arc(0, 12, 8, 0, Math.PI, false);
  ctx.fill();
  ctx.stroke();
  // teeth
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 1.5;
  for (let i = -6; i <= 6; i += 3) {
    ctx.beginPath();
    ctx.moveTo(i, 12);
    ctx.lineTo(i, 19);
    ctx.stroke();
  }

  // bouncy big back wheels
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 3;
  ctx.fillStyle = OUTLINE;
  ctx.beginPath();
  ctx.arc(-15, -14, 6, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(-15, 14, 6, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  // small front wheels
  ctx.beginPath();
  ctx.arc(15, -14, 4, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(15, 14, 4, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

// ============================================================================
// Hotrod (Lightning Wedge) — pointy aerodynamic wedge with rear spoiler.
// ============================================================================
function drawHotrodCar(ctx, x, y, angle, body, accent, alpha = 1) {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.lineJoin = "round";
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 5;

  // shadow
  ctx.fillStyle = "rgba(0,0,0,0.3)";
  ctx.beginPath();
  ctx.ellipse(3, 5, 24, 12, 0, 0, Math.PI * 2);
  ctx.fill();

  // wedge body — pointed nose
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.moveTo(-22, -12);
  ctx.lineTo(14, -10);
  ctx.lineTo(24, 0);
  ctx.lineTo(14, 10);
  ctx.lineTo(-22, 12);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // big white racing stripe down the centre
  ctx.fillStyle = accent;
  ctx.beginPath();
  ctx.moveTo(-22, -3);
  ctx.lineTo(20, -3);
  ctx.lineTo(24, 0);
  ctx.lineTo(20, 3);
  ctx.lineTo(-22, 3);
  ctx.closePath();
  ctx.fill();

  // tinted bubble cockpit
  ctx.fillStyle = "#1f2e4a";
  ctx.beginPath();
  roundRect(ctx, -4, -8, 14, 16, 4);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = "rgba(255,255,255,0.4)";
  ctx.fillRect(-2, -7, 4, 8);

  // big rear spoiler (stick + wing)
  ctx.fillStyle = OUTLINE;
  ctx.fillRect(-22, -2, 4, 4);
  ctx.fillRect(-26, -10, 4, 20);
  ctx.strokeRect(-26, -10, 4, 20);
  ctx.fillStyle = accent;
  ctx.fillRect(-25, -9, 2, 18);

  // headlights (slim pointed)
  ctx.fillStyle = "#FFFAA6";
  ctx.beginPath();
  ctx.moveTo(20, -4);
  ctx.lineTo(24, -1);
  ctx.lineTo(20, 0);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(20, 4);
  ctx.lineTo(24, 1);
  ctx.lineTo(20, 0);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // wheels
  ctx.fillStyle = OUTLINE;
  ctx.fillRect(-18, -16, 10, 5);
  ctx.fillRect(-18, 11, 10, 5);
  ctx.fillRect(6, -16, 10, 5);
  ctx.fillRect(6, 11, 10, 5);
  // tiny rim sparkle
  ctx.fillStyle = accent;
  ctx.fillRect(-15, -15, 2, 1);
  ctx.fillRect(-15, 12, 2, 1);
  ctx.fillRect(9, -15, 2, 1);
  ctx.fillRect(9, 12, 2, 1);

  ctx.restore();
}

// Golden Viper — Globetrotter prize car. Bird's-eye view of a low,
// wide sport coupe (Cayman / Supra / Viper silhouette). Per the user's
// reference photo: matte-black body with metallic-gold accents (twin
// hood-to-trunk stripes, gold wheels, gold side gill, gold lip splitter,
// gold rear spoiler edges).
function drawViperCar(ctx, x, y, angle, body, accent, alpha = 1) {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.lineJoin = "round";
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 4;

  // ground shadow — wider than other cars to read "sports car wide stance"
  ctx.fillStyle = "rgba(0,0,0,0.3)";
  ctx.beginPath();
  ctx.ellipse(2, 6, 28, 13, 0, 0, Math.PI * 2);
  ctx.fill();

  // long curved body — pointed nose, fastback rear, hourglass waist
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.moveTo(-26, -10);     // rear-left corner
  ctx.lineTo(-24, -14);     // rear flare
  ctx.lineTo(-6, -15);      // rear quarter
  ctx.lineTo(8, -13);       // hood flare narrowing
  ctx.lineTo(22, -8);       // front fender
  ctx.lineTo(26, -3);       // front bumper top
  ctx.lineTo(26, 3);        // front bumper bottom
  ctx.lineTo(22, 8);        // front fender
  ctx.lineTo(8, 13);        // hood flare
  ctx.lineTo(-6, 15);       // rear quarter
  ctx.lineTo(-24, 14);      // rear flare
  ctx.lineTo(-26, 10);      // rear-right corner
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // TWIN GOLD STRIPES — running hood-to-trunk, the signature feature
  ctx.fillStyle = accent;
  ctx.fillRect(-26, -7, 52, 3);   // upper stripe
  ctx.fillRect(-26, 4, 52, 3);    // lower stripe

  // tinted cockpit / windshield (shifted forward, like a sports coupe)
  ctx.fillStyle = "#1a2030";
  ctx.beginPath();
  ctx.moveTo(-8, -9);
  ctx.lineTo(10, -7);
  ctx.lineTo(10, 7);
  ctx.lineTo(-8, 9);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  // windshield glare
  ctx.fillStyle = "rgba(255,255,255,0.25)";
  ctx.fillRect(2, -6, 6, 12);

  // SIDE GILL — short gold slash on each side, just behind the cockpit
  // (the signature gold "gash" mark from the reference photo)
  ctx.fillStyle = accent;
  ctx.fillRect(-14, -12, 8, 2);
  ctx.fillRect(-14, 10, 8, 2);

  // gold lip splitter at the front
  ctx.fillStyle = accent;
  ctx.beginPath();
  ctx.moveTo(22, -4);
  ctx.lineTo(28, -2);
  ctx.lineTo(28, 2);
  ctx.lineTo(22, 4);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // gold rear spoiler — flat ducktail across the rear deck
  ctx.fillStyle = accent;
  ctx.fillRect(-28, -10, 4, 20);
  ctx.strokeRect(-28, -10, 4, 20);

  // headlights — slim LED slits with a yellow glow
  ctx.fillStyle = "#FFFAA6";
  ctx.fillRect(20, -7, 4, 2);
  ctx.fillRect(20, 5, 4, 2);

  // taillights — red LED bars
  ctx.fillStyle = "#FF3030";
  ctx.fillRect(-26, -9, 2, 4);
  ctx.fillRect(-26, 5, 2, 4);

  // GOLD WHEELS — rims. Black tire walls, accent-gold rim
  ctx.fillStyle = OUTLINE;
  ctx.fillRect(-20, -17, 12, 5);  // rear-top tire
  ctx.fillRect(-20, 12, 12, 5);   // rear-bottom tire
  ctx.fillRect(8, -17, 12, 5);    // front-top tire
  ctx.fillRect(8, 12, 12, 5);     // front-bottom tire
  // gold rim accents (visible portion of the wheel)
  ctx.fillStyle = accent;
  ctx.fillRect(-18, -16, 8, 3);
  ctx.fillRect(-18, 13, 8, 3);
  ctx.fillRect(10, -16, 8, 3);
  ctx.fillRect(10, 13, 8, 3);

  ctx.restore();
}
